<script>
import IconComponent from './Icon.vue'
import map from 'lodash/map'
import toSafeInteger from 'lodash/toSafeInteger'

let uuid = 0

export default {
    // state
    data () {
        return {
            adults: 0,
            children: [],
            id: uuid++,
            open: false,
        }
    },
    props: {
        guests: {
            default: { adults: 2, children: [] },
            type: Object,
        },
    },
    computed: {
        adultsText () {
            const { adults } = this
            return `${adults} Adult${1 === adults ? '' : 's'}`
        },
        childrenText () {
            const children = this.children.length
            if (!children) {
                return 'No children'
            }
            return `${children} Child${1 === children ? '' : 'ren'}`
        },
        summaryText () {
            const total = this.adults + this.children.length
            return `${total} Guest${1 === total ? '' : 's'}`
        },
    },
    methods: {
        emitUpdate () {
            this.$emit('updateGuests', {
                detail: {
                    adults: this.adults,
                    children: this.children,
                },
            })
        },
        close () {
            this.open = false
        },
        documentClick ($event) {
            if (this.$el.contains($event.target)) return
            this.close()
        },
        childAgeLabel (index) {
            return `Age of Child ${index + 1}`
        },
        childAgeText (index) {
            const age = this.children[index]
            switch (age) {
                case 0:
                return '<1 Year'
                case 1:
                return '1 Year'
                default:
                return `${age} Years`
            }
        },
        incrementAdults (increment) {
            this.adults = Math.min(Math.max(this.adults + increment, 1), 12)
        },
        incrementChildren (increment) {
            if (increment > 0) {
                for (let i = 0; i < increment; i++) {
                    this.children.push(0)
                }
            } else if (increment < 0) {
                for (let i = 0; i < Math.abs(increment); i++) {
                    this.children.pop()
                }
            }
        },
        incrementChildAge (index, increment) {
            this.children[index] = Math.min(
                Math.max(this.children[index] + increment, 0),
                17
            )
        },
    },
    watch: {
        adults (x) {
            this.children = x > 0 ? this.children : []
            this.emitUpdate()
        },
        children () {
            this.emitUpdate()
        },
    },
    // lifecycle
    mounted () {
        this.adults = toSafeInteger(this.guests.adults) || 2
        this.children = map(this.guests.children, toSafeInteger)
        document.addEventListener('click', this.documentClick)
    },
    unmounted () {
        document.removeEventListener('click', this.documentClick)
    },
    // misc.
    components: {
        Icon: IconComponent,
    },
}
</script>

<template>
    <div class="tw-relative">
        <div
            class="
                tw-relative
                tw-z-0
                tw-flex
                tw-justify-between
                tw-items-center
                tw-px-4
                tw-py-2
                tw-space-x-2
                tw-bg-white
                tw-border
                tw-border-solid
                tw-border-ccc
                tw-cursor-pointer
            "
        >
            <span roll="status" v-text="summaryText"></span>
            <Icon
                aria-hidden="true"
                class="tw-ml-2 tw-fill-vacatia-blue tw-transition-all"
                v-bind:icon="'arrow'"
                v-bind:class="{ 'tw-rotate-180': open }"
            />
            <label
                class="tw-sr-only"
                v-bind:for="'vac_nogs_toggle_' + id"
            >Select Number of Guests</label>
            <button
                aria-haspopup="true"
                class="
                    tw-block
                    tw-absolute
                    tw-z-10
                    tw-inset-0
                    tw-indent-[-9999em]
                    tw-bg-[rgba(255,0,0,0)]
                    tw-border-none
                "
                v-on:click.prevent="open = !open"
                v-bind:aria-expanded="open"
            >toggle guest menu</button>
        </div>
        <div
            class="
                tw-z-40
                tw-px-4
                tw-py-4
                tw-space-y-4
                tw-w-full
                tw-border
                tw-border-solid
                tw-border-ccc
                tw-border-d5
                tw-border-t-0
                tw-bg-white
                lg:tw-absolute
                lg:tw-p-3
                lg:tw-mt-1
                lg:tw-text-xs
                lg:tw-border-t
            "
            v-show="open"
            v-on:click.stop
        >
            <div class="tw-space-y-1">
                <div class="tw-text-xs tw-text-zinc-400">Adults (18+)</div>
                <div class="tw-flex tw-flex-row tw-w-full tw-items-stretch">
                    <button
                        v-on:click.prevent="incrementAdults(-1)"
                        class="tw-text-zinc-600 tw-border tw-py-3 tw-px-4 lg:tw-px-3 tw-border-solid tw-border-zinc-400 tw-font-medium tw-rounded-l tw-bg-white hover:tw-text-zinc-800"
                    >
                        &minus;
                    </button>
                    <span v-text="adultsText" class="tw-grow tw-border-zinc-400 tw-border-solid tw-border-0 tw-border-t tw-border-b tw-p-3 lg:tw-px-2 tw-bg-white"></span>
                    <button
                        v-on:click.prevent="incrementAdults(1)"
                        class="tw-text-zinc-600 tw-border tw-py-3 tw-px-4 lg:tw-px-3 tw-border-solid tw-border-zinc-400 tw-font-medium tw-rounded-r tw-bg-white hover:tw-text-zinc-800"
                    >
                        &plus;
                    </button>
                </div>
            </div>

            <div class="tw-space-y-1">
                <div class="tw-text-xs tw-text-zinc-400">Children (0-17)</div>
                <div class="tw-flex tw-flex-row tw-w-full tw-items-stretch">
                    <button
                        v-on:click.prevent="incrementChildren(-1)"
                        class="tw-text-zinc-600 tw-border tw-py-3 tw-px-4 lg:tw-px-3 tw-border-solid tw-border-zinc-400 tw-font-medium tw-rounded-l tw-bg-white hover:tw-text-zinc-800"
                    >
                        &minus;
                    </button>
                    <span v-text="childrenText" class="tw-grow tw-border-zinc-400 tw-border-solid tw-border-0 tw-border-t tw-border-b tw-p-3 lg:tw-px-2 tw-bg-white"></span>
                    <button
                        v-on:click.prevent="incrementChildren(1)"
                        class="tw-text-zinc-600 tw-border tw-py-3 tw-px-4 lg:tw-px-3 tw-border-solid tw-border-zinc-400 tw-font-medium tw-rounded-r tw-bg-white hover:tw-text-zinc-800"
                    >
                        &plus;
                    </button>
                </div>
            </div>

            <div class="tw-space-y-1" v-for="(child, index) in children" v-bind:key="index">
                <div v-text="childAgeLabel(index)" class="tw-text-xs tw-text-zinc-400"></div>
                <div class="tw-flex tw-flex-row tw-w-full tw-items-stretch">
                    <button
                        v-on:click.prevent="incrementChildAge(index, -1)"
                        class="tw-text-zinc-600 tw-border tw-py-3 tw-px-4 lg:tw-px-3 tw-border-solid tw-border-zinc-400 tw-font-medium tw-rounded-l tw-bg-white hover:tw-text-zinc-800"
                    >
                        &minus;
                    </button>
                    <span v-text="childAgeText(index)" class="tw-grow tw-border-zinc-400 tw-border-solid tw-border-0 tw-border-t tw-border-b tw-p-3 tw-bg-white"></span>
                    <button
                        v-on:click.prevent="incrementChildAge(index, 1)"
                        class="tw-text-zinc-600 tw-border tw-py-3 tw-px-4 lg:tw-px-3 tw-border-solid tw-border-zinc-400 tw-font-medium tw-rounded-r tw-bg-white hover:tw-text-zinc-800"
                    >
                        &plus;
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
